import { gql } from '@apollo/client';
import {
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  chakra,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import capitalize from 'lodash/capitalize';
import { Fragment, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { Columns } from '~components/displays/DisplayTable/constants';
import {
  FilterContext,
  usePersistentFilters,
} from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { FilterOptionPersisted } from '~components/displays/DisplayTable/persistantFilters/persistentFiltersModel';
import { EmptyView, EmptyViewButton } from '~components/EmptyView';
import { AddScheduleTimeSlotModal } from '~components/schedules/AddScheduleTimeSlotModal';
import { ClearScheduleDayModal } from '~components/schedules/ClearScheduleDayModal';
import { DeleteSchedulesModal } from '~components/schedules/DeleteSchedulesModal';
import { SyncScheduleDisplaysModal } from '~components/schedules/SyncScheduleDisplaysModal';
import { useRemoveScheduleFromDisplays } from '~components/schedules/useRemoveScheduleFromDisplays';
import { ActionMenuButton } from '~components/ui/ActionMenuButton';
import { InfoAlert, WarningAlert } from '~components/ui/Alert';
import { BackButton } from '~components/ui/BackButton';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { DisplaysDetailStats } from '~components/ui/DisplaysDetailStats';
import { EmptyPowerScheduleIllustration } from '~components/ui/illustrations';
import { PageHeading } from '~components/ui/PageHeading';
import {
  Day,
  ScheduleTimeBlockInput,
  TimeBlockCategory,
  TimeBlockType,
} from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { useContentBoxWidth } from '~utils/dimensions';
import { fromError } from '~utils/errors';
import { useFeatureFlag } from '~utils/features';
import { useSubscriptionType } from '~utils/subscriptions';
import { DAYS_OF_WEEK_MON_FIRST } from '~utils/timeBlocks';
import { MaybePromise } from '~utils/types';
import { useLimits } from '~utils/useLimits';
import { ScheduleStackedIcon, ScheduleStandbyIcon } from '../icons';
import {
  assignTimeBlockPriorities,
  getTimeBlockCategory,
  MAX_UNIQUE_PLAYLISTS_PER_SCHEDULE,
  timeBlockTypesForCategory,
  toMinutes,
  toScheduleTimeBlockInput,
} from '../scheduleTimeBlocks';
import { TIME_BLOCK_META } from '../timeBlockMeta';
import { AppOption, PlaylistOption } from '../useEditScheduleTimeBlocks';
import {
  ScheduleDetail_CustomerFragment,
  ScheduleDetail_ScheduleFragment,
  useScheduleDuplicateMutation,
  useScheduleUpdateMutation,
} from './__generated__/ScheduleDetail.graphql';
import { EditScheduleModal } from './EditScheduleModal';
import { EditScheduleTimeSlotsModal } from './EditScheduleTimeSlotsModal';
import { ScheduleDetailMoreActions } from './ScheduleDetailMoreActions';
import { ScheduleTimeBlockBox } from './ScheduleTimeBlockBox';

const CATEGORY_TABS = [TimeBlockCategory.Content, TimeBlockCategory.Settings];

const blockKeyOf = (block: ScheduleTimeBlockInput) =>
  `${block.day}|${block.type}|${block.priority}|${block.start}|${block.end}`;

/**
 * Width of the gutter that always shows the standby icon. Taken from the design,
 * where the day's slots start at 36px while the row's track starts at 4px.
 */
const TIMELINE_STANDBY_GUTTER = '32px';

interface Props {
  customer: ScheduleDetail_CustomerFragment;
  schedule: ScheduleDetail_ScheduleFragment;
  /** The organization's app subscriptions, the pool an APPS slot can point at. */
  apps: AppOption[];
  onScheduleUpdateSuccess: () => MaybePromise<void>;
}

export function ScheduleDetail({ customer, schedule, apps, onScheduleUpdateSuccess }: Props) {
  const toast = useToast();
  const navigate = useNavigate();
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions, user } = useAuth();
  const { customer: customerHandle } = useParams();
  const { resetCustomerFilters, setColumnFilters } = usePersistentFilters(
    user?.subId ?? '',
    customerHandle ?? '',
    FilterContext.DISPLAYS,
    customer.id,
  );

  const editScheduleModal = useDisclosure();
  const addTimeSlotModal = useDisclosure();
  const deleteSchedulesModal = useDisclosure();
  const syncDisplaysModal = useDisclosure();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  // The ruler's content box spans exactly the block area, so measuring it once gives
  // every block the pixel width it needs to decide what still fits.
  const rulerRef = useRef<HTMLDivElement>(null);
  const timelineWidth = useContentBoxWidth(rulerRef);
  // Only one slot editor is open at a time, and the row menu can open one too.
  const [dayToClear, setDayToClear] = useState<Day | undefined>();
  const [dayToEdit, setDayToEdit] = useState<Day | undefined>();
  const [selectedTimeBlock, setSelectedTimeBlock] = useState<ScheduleTimeBlockInput | undefined>();

  /** Clicking a slot opens the day's stack on it; the stacked icon opens with none. */
  const openStackedEditor = (day: Day, block?: ScheduleTimeBlockInput) => {
    setSelectedTimeBlock(block);
    setDayToEdit(day);
  };

  const closeStackedEditor = () => {
    setDayToEdit(undefined);
    setSelectedTimeBlock(undefined);
  };
  // Set when the dialog is opened from a legend tag, so its type comes preselected.
  const [pendingType, setPendingType] = useState<TimeBlockType | undefined>();

  const { removeScheduleFromDisplays } = useRemoveScheduleFromDisplays();
  const [duplicateSchedule] = useScheduleDuplicateMutation();
  const [updateSchedule] = useScheduleUpdateMutation();

  const hasScheduleUpdatePermission = verifyUserPermissions([Permission.PowerscheduleUpdate]);

  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');
  const { hasWaveSubscription: hasEssentialSubscription } = useSubscriptionType(customer);
  const {
    schedules: {
      schedulesNumberPerCustomerLimit: schedulesLimit,
      isSchedulesLimitPerCustomerReached,
    },
  } = useLimits();
  const isSchedulesLimitReached = isSchedulesLimitPerCustomerReached(
    customer.schedules.slice(1),
    hasEssentialSubscription,
  );
  const canAddTimeSlot = hasScheduleUpdatePermission && !isSchedulesLimitReached;

  const playlists = useMemo<PlaylistOption[]>(
    () =>
      customer.playlists.map((playlist) => ({
        id: playlist.id,
        title: playlist.title,
        mediaType: playlist.playlistType,
      })),
    [customer.playlists],
  );

  const timeBlocks = useMemo<ScheduleTimeBlockInput[]>(
    () => schedule.timeBlocks.map(toScheduleTimeBlockInput),
    [schedule.timeBlocks],
  );

  const activeCategory = CATEGORY_TABS[activeTabIndex];
  const blocksInCategory = timeBlocks.filter(
    (block) => getTimeBlockCategory(block.type) === activeCategory,
  );

  const handleGoBack = () => navigate('../schedules');

  const handleDeleteSchedulesSuccess = () => {
    deleteSchedulesModal.onClose();
    analytics.track('scheduleDelete');
    navigate('../schedules');
  };

  const canGoToLinkedDisplays = schedule.syncedDisplays.length > 0;

  const handleGoToLinkedDisplays = async (
    filters: Partial<Record<Columns, FilterOptionPersisted>> = {},
  ) => {
    await resetCustomerFilters();
    await setColumnFilters(Columns.PowerSchedule, [{ id: schedule.id }]);

    for (const [key, value] of Object.entries(filters)) {
      await setColumnFilters(key, [value]);
    }

    navigate('../displays');
  };

  const updateScheduleTimeBlocks = async (blocks: ScheduleTimeBlockInput[]) => {
    try {
      await updateSchedule({
        variables: {
          input: {
            scheduleId: schedule.id,
            /*
             * The stack rests on the bottom lane, so a slot dragged into a lane with
             * nothing under it belongs one lane lower. Packing here rather than only
             * when the editor opens keeps what is stored and what is drawn the same;
             * the callers that already pack are unaffected, since this is idempotent.
             */
            timeBlocks: assignTimeBlockPriorities(blocks),
          },
        },
      });
      await onScheduleUpdateSuccess();
      toast({ status: 'success', title: t('scheduleSaved') });
    } catch (err) {
      toast({
        status: 'error',
        title: t('scheduleSaveErr'),
        description: fromError(err, 'UpdateSchedule', {
          SCHEDULE_NOT_FOUND: t('schedulesDeleteErrDesc'),
          SCHEDULE_EMPTY_TITLE: t('scheduleErrDesc1'),
          SCHEDULE_TOO_MANY_SLOTS_PER_DAY: t('scheduleSaveErrTooManySlots'),
          SCHEDULE_TOO_MANY_PLAYLISTS: t('scheduleSlotErrMaxPlaylists', {
            limit: MAX_UNIQUE_PLAYLISTS_PER_SCHEDULE,
          }),
          SCHEDULE_INVALID_SLOT_DURATION: t('scheduleSaveErrSlotDuration'),
          SCHEDULE_OVERLAPPING_TIME_SLOTS: t('scheduleSaveErrOverlap'),
          SCHEDULE_INVALID_TIME_BLOCK: t('scheduleSaveErrInvalidSlot'),
        }),
      });
    }
  };

  const addTimeSlot = (type?: TimeBlockType) => {
    analytics.track('scheduleTimeSlotCreateStart');
    setPendingType(type);
    addTimeSlotModal.onOpen();
  };

  const timeSlotAdded = async (blocks: ScheduleTimeBlockInput[], category: TimeBlockCategory) => {
    analytics.track('scheduleTimeSlotCreateComplete');
    addTimeSlotModal.onClose();
    await updateScheduleTimeBlocks(blocks);

    // The slot may have been created for the other track, in which case the new
    // block would land on a tab the user cannot see. Follow it.
    const categoryIndex = CATEGORY_TABS.indexOf(category);
    if (categoryIndex !== -1) {
      setActiveTabIndex(categoryIndex);
    }
  };

  const timeSlotEdited = async (blocks: ScheduleTimeBlockInput[]) => {
    analytics.track('scheduleTimeSlotUpdate');
    await updateScheduleTimeBlocks(blocks);
  };

  const handleScheduleEdit = async () => {
    await onScheduleUpdateSuccess();
    editScheduleModal.onClose();
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await duplicateSchedule({
        variables: { input: { scheduleId: schedule.id } },
      });

      if (!data || !data.scheduleDuplicate.schedule) {
        throw new Error('Duplicate schedule failed');
      }

      toast({ status: 'success', title: t('scheduleDuplicated') });
    } catch (err) {
      toast({
        status: 'error',
        title: t('scheduleDuplicateErr'),
        description: fromError(err, 'ScheduleDuplicate', {
          SCHEDULE_LIMIT_REACHED: t('scheduleErrDesc'),
          WAVE_SUBSCRIPTION_SCHEDULES_LIMIT_REACHED: t('scheduleErrDesc2'),
        }),
      });
    }
  };

  const removeDisplaysAction = useDestructiveAction<undefined>({
    title: t('removeScheduleDisplays'),
    message: t('removeScheduleDisplaysDesc'),
    notice: t('removeScheduleDisplaysNotice'),
    confirmLabel: t('remove'),
    onConfirm: async () => {
      await removeScheduleFromDisplays(schedule.id);
      await onScheduleUpdateSuccess();
    },
  });

  // Clearing a day only clears the tab the user is looking at: content and settings
  // blocks are independent tracks, and the other track stays untouched.
  const handleClearDay = async (day: Day) => {
    setDayToClear(undefined);
    await updateScheduleTimeBlocks(
      assignTimeBlockPriorities(
        timeBlocks.filter(
          (block) => !(block.day === day && getTimeBlockCategory(block.type) === activeCategory),
        ),
      ),
    );
  };

  /**
   * True when two of a day's slots cover the same minute. Same-lane overlap is
   * rejected on save, so this only happens across priority lanes - which is exactly
   * what the stacked indicator reports.
   */
  const hasOverlappingBlocks = (blocks: ScheduleTimeBlockInput[]) =>
    blocks.some((block, i) =>
      blocks.some(
        (other, j) =>
          i !== j &&
          toMinutes(block.start, 'start') < toMinutes(other.end, 'end') &&
          toMinutes(other.start, 'start') < toMinutes(block.end, 'end'),
      ),
    );

  return (
    <>
      <PageHeading
        floatingButton={<BackButton onClick={handleGoBack} />}
        description={schedule.description || undefined}
        actions={
          <HStack>
            <Button
              variant="solid"
              colorScheme="blue"
              onClick={() => addTimeSlot()}
              isDisabled={!canAddTimeSlot}
            >
              {t('addTimeSlot')}
            </Button>
            <ScheduleDetailMoreActions
              schedule={schedule}
              isSchedulesLimitReached={isSchedulesLimitReached}
              onRemoveDisplays={() => removeDisplaysAction.askConfirmation(undefined)}
              onDelete={deleteSchedulesModal.onOpen}
              onSync={syncDisplaysModal.onOpen}
              onRename={editScheduleModal.onOpen}
              onDuplicate={handleDuplicate}
            />
          </HStack>
        }
      >
        {schedule.title}
      </PageHeading>

      {isSchedulesLimitReached && (
        <WarningAlert marginBottom="6">
          <AlertTitle>{t('schedulesLimitReached')}</AlertTitle>
          <AlertDescription>
            {isSubscriptionsEnabled && !hasEssentialSubscription
              ? t('schedulesLimitReachedAlert')
              : t('schedulesLimitMessage', { schedulesLimit })}
          </AlertDescription>
        </WarningAlert>
      )}

      <Box marginBottom={8}>
        <DisplaysDetailStats
          syncedCount={schedule.syncedDisplays.length}
          outOfSyncCount={schedule.outOfSyncDisplays.length}
          syncingCount={schedule.syncingDisplays.length}
          removingCount={schedule.removingDisplays.length}
          onClickSyncedDisplays={
            canGoToLinkedDisplays ? () => handleGoToLinkedDisplays() : undefined
          }
          onClickOutOfSyncDisplays={() =>
            handleGoToLinkedDisplays({
              [Columns.Warnings]: {
                id: 'isScheduleOutOfSync',
              },
            })
          }
        />
      </Box>

      <Tabs index={activeTabIndex} onChange={setActiveTabIndex} variant="line" colorScheme="blue">
        <TabList marginBottom="8">
          {CATEGORY_TABS.map((category) => (
            <Tab key={category} fontWeight="semibold">
              {t(category === TimeBlockCategory.Content ? 'content' : 'settings')}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {CATEGORY_TABS.map((category) => (
            <TabPanel key={category} padding="0">
              <HStack spacing="8" marginBottom="6" flexWrap="wrap">
                {timeBlockTypesForCategory(category).map((type) => {
                  const meta = TIME_BLOCK_META[type];
                  const LegendIcon = meta.icon;
                  return (
                    <HStack
                      key={type}
                      spacing="2"
                      as="button"
                      type="button"
                      onClick={() => addTimeSlot(type)}
                      disabled={!canAddTimeSlot}
                      cursor={canAddTimeSlot ? 'pointer' : 'not-allowed'}
                    >
                      <LegendIcon width={6} height={6} color={meta.accent} />
                      <chakra.span color="blue.800" fontSize="sm">
                        {t(meta.label)}
                      </chakra.span>
                    </HStack>
                  );
                })}
              </HStack>

              {schedule.isLegacy && category === TimeBlockCategory.Content && (
                <InfoAlert marginBottom="6">{t('scheduleLegacyPowerScheduleNotice')}</InfoAlert>
              )}

              <Grid
                templateColumns="[days] auto [padding-left] 56px [timeline] 1fr [padding-right] 16px [stacked] auto [gap] 8px [actions] auto"
                rowGap={4}
                alignItems="center"
              >
                <GridItem
                  ref={rulerRef}
                  gridColumnStart="timeline"
                  display="flex"
                  justifyContent="space-between"
                  marginBottom="-2"
                  // Offset by the standby gutter plus the row's own inset so the ticks
                  // line up with the block area.
                  paddingLeft={`calc(${TIMELINE_STANDBY_GUTTER} + 4px)`}
                  paddingRight="4px"
                >
                  <span>00:00</span>
                  <span>06:00</span>
                  <span>12:00</span>
                  <span>18:00</span>
                  <span>00:00</span>
                </GridItem>
                {blocksInCategory.length === 0 && (
                  <GridItem
                    gridColumnStart="timeline"
                    rowStart={2}
                    rowEnd={DAYS_OF_WEEK_MON_FIRST.length + 2}
                    textAlign="center"
                    zIndex={2}
                    padding={8}
                  >
                    <EmptyView
                      icon={<EmptyPowerScheduleIllustration />}
                      title={t('emptySchedule')}
                      description={t('emptyScheduleDesc')}
                      padding="24px 0 0 0"
                    >
                      <EmptyViewButton
                        onClick={() => addTimeSlot()}
                        label={t('addTimeSlot')}
                        isDisabled={!canAddTimeSlot}
                      />
                    </EmptyView>
                  </GridItem>
                )}
                {DAYS_OF_WEEK_MON_FIRST.map((DAY, i) => {
                  const blocksForDay = blocksInCategory.filter(({ day }) => day === DAY);
                  const hasBlocksForDay = blocksForDay.length > 0;
                  const isStacked = hasOverlappingBlocks(blocksForDay);

                  return (
                    <Fragment key={DAY}>
                      <GridItem
                        fontFamily="Gilroy, sans-serif"
                        fontWeight={600}
                        fontSize="20px"
                        gridColumnStart="days"
                        rowStart={i + 2}
                      >
                        {capitalize(t(DAY))}
                      </GridItem>
                      <GridItem
                        gridColumnStart="timeline"
                        rowStart={i + 2}
                        h="12"
                        bg="white"
                        borderRadius={4}
                        borderColor="gray.100"
                        borderWidth={1}
                        borderStyle="solid"
                        padding="4px"
                        filter="auto"
                        blur={blocksInCategory.length > 0 ? 0 : '3px'}
                      >
                        <Flex height="100%" background="gray.50" borderRadius={4}>
                          {/*
                           * Standby keeps its own gutter so it stays visible even when a
                           * slot covers the whole day. 00:00 therefore starts to its right.
                           */}
                          <Flex
                            width={TIMELINE_STANDBY_GUTTER}
                            flexShrink={0}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Tooltip label={t('standbyTooltip')} hasArrow>
                              <ScheduleStandbyIcon width={4} height={4} color="gray.500" />
                            </Tooltip>
                          </Flex>
                          <Box position="relative" flex="1" height="100%">
                            {blocksForDay.map((block) => (
                              <ScheduleTimeBlockBox
                                currentTimeBlock={block}
                                playlists={playlists}
                                apps={apps}
                                key={blockKeyOf(block)}
                                isDisabled={!hasScheduleUpdatePermission}
                                timelineWidth={timelineWidth}
                                onSelect={(selected) => openStackedEditor(DAY, selected)}
                              />
                            ))}
                          </Box>
                        </Flex>
                      </GridItem>
                      <GridItem
                        gridColumnStart="stacked"
                        rowStart={i + 2}
                        display="flex"
                        justifyContent="center"
                      >
                        <Tooltip
                          label={isStacked ? t('slotsStacked') : t('slotsNotStacked')}
                          hasArrow
                        >
                          <IconButton
                            aria-label={t('viewAndEdit')}
                            variant="ghost"
                            size="sm"
                            isDisabled={!hasBlocksForDay}
                            onClick={() => openStackedEditor(DAY)}
                            icon={
                              <ScheduleStackedIcon
                                width={6}
                                height={6}
                                color={isStacked ? 'blue.700' : 'gray.200'}
                              />
                            }
                          />
                        </Tooltip>
                      </GridItem>
                      <GridItem gridColumnStart="actions" rowStart={i + 2}>
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={ActionMenuButton}
                            sx={{
                              filter: 'auto',
                              blur: blocksInCategory.length > 0 ? 0 : '2px',
                            }}
                            disabled={blocksInCategory.length === 0}
                          />
                          <MenuList>
                            <MenuItem
                              onClick={() => openStackedEditor(DAY)}
                              isDisabled={!hasBlocksForDay}
                            >
                              {t('viewAndEdit')}
                            </MenuItem>
                            <MenuItem
                              onClick={() => setDayToClear(DAY)}
                              isDisabled={!hasScheduleUpdatePermission || !hasBlocksForDay}
                            >
                              {t('clear')}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </GridItem>
                    </Fragment>
                  );
                })}
              </Grid>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {removeDisplaysAction.confirmationNode}
      <AddScheduleTimeSlotModal
        timeBlocks={timeBlocks}
        defaultCategory={activeCategory}
        defaultType={pendingType}
        playlists={playlists}
        inputSources={customer.inputSources}
        apps={apps}
        isOpen={addTimeSlotModal.isOpen}
        onCancel={addTimeSlotModal.onClose}
        onSubmit={timeSlotAdded}
      />
      <SyncScheduleDisplaysModal
        isOpen={syncDisplaysModal.isOpen}
        schedule={schedule}
        onSuccess={syncDisplaysModal.onClose}
        onCancel={syncDisplaysModal.onClose}
      />
      <DeleteSchedulesModal
        isOpen={deleteSchedulesModal.isOpen}
        schedules={[schedule]}
        onCancel={deleteSchedulesModal.onClose}
        onSuccess={handleDeleteSchedulesSuccess}
      />
      <EditScheduleTimeSlotsModal
        isOpen={!!dayToEdit}
        day={dayToEdit}
        selectedTimeBlock={selectedTimeBlock}
        category={activeCategory}
        timeBlocks={timeBlocks}
        playlists={playlists}
        inputSources={customer.inputSources}
        apps={apps}
        isDisabled={!hasScheduleUpdatePermission}
        onCancel={closeStackedEditor}
        onSubmit={async (blocks) => {
          await timeSlotEdited(blocks);
          closeStackedEditor();
        }}
      />
      <ClearScheduleDayModal
        isOpen={!!dayToClear}
        onCancel={() => setDayToClear(undefined)}
        onConfirm={() => dayToClear && handleClearDay(dayToClear)}
      />
      <EditScheduleModal
        isOpen={editScheduleModal.isOpen}
        schedule={schedule}
        onCancel={editScheduleModal.onClose}
        onSuccess={handleScheduleEdit}
      />
    </>
  );
}

ScheduleDetail.graphql = {
  fragments: {
    ScheduleDetail_customer: gql`
      fragment ScheduleDetail_customer on Customer {
        id
        name
        inputSources
        playlists {
          id
          title
          playlistType
        }
        schedules {
          id
        }
        ...CustomerSubscriptionData_customer
      }
    `,
    ScheduleDetail_schedule: gql`
      fragment ScheduleDetail_schedule on Schedule {
        id
        title
        description
        isLegacy
        isScheduleUsedByAnyConfiguration
        timeBlocks {
          day
          start
          end
          type
          priority
          category
          data {
            applicationId
            id
            mediaType
            source
            url
            value
          }
        }
        allDisplays: displays {
          id
        }
        syncedDisplays: displays(filter: { state: SYNCED }) {
          id
        }
        outOfSyncDisplays: displays(filter: { state: OUT_OF_SYNC }) {
          id
        }
        syncingDisplays: displays(filter: { state: SYNCING }) {
          id
        }
        removingDisplays: displays(filter: { state: REMOVING }) {
          id
        }
        ...ScheduleDetailMoreActions_schedule
        ...SyncScheduleDisplaysModal_schedule
        ...DeleteSchedulesModal_schedule
        ...EditScheduleModal_schedule
      }
    `,
  },
  mutations: {
    ScheduleDuplicate: gql`
      mutation ScheduleDuplicate($input: ScheduleDuplicateInput!) {
        scheduleDuplicate(input: $input) {
          schedule {
            id
          }
        }
      }
    `,
    ScheduleUpdate: gql`
      mutation ScheduleUpdate($input: ScheduleUpdateInput!) {
        scheduleUpdate(input: $input) {
          schedule {
            id
            ...ScheduleDetail_schedule
          }
        }
      }
    `,
  },
};
